import { useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { AppButton } from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'
import { MessagingMethodsBottomSheet } from '@/components/MessagingMethodsBottomSheet'
import { usePhoneValidation } from '@/hooks'
import { useSignInWithPhone, useSendCodeToPhone } from '@/domain'
import { IPhoneDataSchema, usePhoneDataSchema } from '@/schemas'
import { useAuthPreferencesStore } from '@/store'
import { useAuthScopedStore } from '@/store/scoped'
import { useGetCountriesCatalog } from '@/domain/countries/useCases'
import type { PhoneData } from '@/types'
import type { MessagingMethodCode } from '@/domain/messagingMethods/messagingMethodsTypes'

export const IdentifyWithPhoneForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const setPhone = useAuthScopedStore((state) => state.setPhone)
  const setIdentificationMethod = useAuthScopedStore(
    (state) => state.setIdentificationMethod,
  )
  const setMessagingMethod = useAuthScopedStore(
    (state) => state.setMessagingMethod,
  )
  const setSignUpForm = useAuthScopedStore((state) => state.setSignUpForm)
  const setSignInForm = useAuthScopedStore((state) => state.setSignInForm)

  const setAuthPreferences = useAuthPreferencesStore(
    (state) => state.setAuthPreferences,
  )

  const { data: countriesData, isLoading: isLoadingCountries } =
    useGetCountriesCatalog()

  const getCountryIsoFromCode = (countryCode: string): string => {
    if (!countriesData?.countries) {
      return '' // now defaulting
    }

    const code = countryCode.replace('+', '')
    const codeNumber = parseInt(code, 10)

    const foundCountry = countriesData.countries.find(
      (country) => country.code === codeNumber,
    )
    return foundCountry?.short_name || ''
  }

  // Initialize phone data from preferences once
  const [currentPhoneData, setCurrentPhoneData] = useState<PhoneData | null>(
    () => {
      const { countryCode, countryIsoCode, number } =
        useAuthPreferencesStore.getState()

      // If we have countryCode and number, create phone data
      if (countryCode && number) {
        const derivedIsoCode =
          countryIsoCode || getCountryIsoFromCode(countryCode)

        return {
          countryCode,
          countryIsoCode: derivedIsoCode,
          number,
        }
      }
      return null
    },
  )

  const [selectedMessagingMethod, setSelectedMessagingMethod] =
    useState<MessagingMethodCode | null>(null)
  const [showMessagingMethodsSheet, setShowMessagingMethodsSheet] =
    useState(false)
  const [pendingSignUpData, setPendingSignUpData] = useState<{
    userId: string
    phoneNumber: string
  } | null>(null)

  const { phoneIsValid, errorMessage, completePhone } = usePhoneValidation({
    phone: currentPhoneData,
    options: { autoValidate: true, debounceDelay: 600 },
  })

  // Save derived countryIsoCode when API data loads or if it was missing
  useEffect(() => {
    const { countryCode, countryIsoCode, number } =
      useAuthPreferencesStore.getState()
    if (countryCode && number && !countryIsoCode && countriesData?.countries) {
      const derivedIsoCode = getCountryIsoFromCode(countryCode)
      if (derivedIsoCode !== 'KW' || countryCode === '+965') {
        // Only save if we found a match or it's actually Kuwait
        setAuthPreferences({ countryIsoCode: derivedIsoCode })
      }
    }
  }, [countriesData?.countries, setAuthPreferences])

  const phoneDataSchema = usePhoneDataSchema()

  const { handleSubmit, formState, setValue, getValues, trigger } =
    useForm<IPhoneDataSchema>({
      resolver: zodResolver(phoneDataSchema),
      defaultValues: {
        phoneData: currentPhoneData || {
          countryCode: '',
          countryIsoCode: '',
          number: '',
        },
      },
      mode: 'onChange', // Change to onChange for better validation feedback
    })

  // Trigger validation when phone data is loaded from localStorage
  useEffect(() => {
    if (currentPhoneData?.number && currentPhoneData?.countryCode) {
      trigger('phoneData')
    }
  }, [currentPhoneData, trigger])

  const { mutateAsync: signInWithPhone, isPending: isCheckingPhone } =
    useSignInWithPhone({
      onSuccess: ({ user_id, next_step }) => {
        // Existing user - verify password
        if (next_step === 'VERIFY_PASSWORD') {
          setSignInForm({
            userId: user_id,
          })
          navigate('/auth/enter-password')
        }
        // New user - verify code
        else if (next_step === 'SEND_CODE_BY_PHONE') {
          setSignUpForm({
            userId: user_id,
          })

          // Store pending signup data and show messaging method selection
          setPendingSignUpData({
            userId: user_id,
            phoneNumber: completePhone,
          })
          setShowMessagingMethodsSheet(true)
        }
        // Setup profile
        else if (next_step === 'SETUP_PROFILE') {
          setSignUpForm({
            userId: user_id,
          })
          navigate('/auth/setup-profile')
        }
      },
      onError: ({ errorMessage }) => {
        toast.error(errorMessage || 'Something went wrong1')
      },
    })

  const { mutateAsync: sendCodeToPhone, isPending: isSendingCode } =
    useSendCodeToPhone({
      onSuccess: ({ user_id }) => {
        setSignUpForm({
          userId: user_id,
        })
        navigate('/auth/signup/enter-verification-code')
      },
      onError: ({ errorMessage }) => {
        toast.error(errorMessage || 'Failed to send code')
      },
    })

  const handleSubmitForm = async (formData: IPhoneDataSchema) => {
    setPhone(formData.phoneData)
    setAuthPreferences({
      signInMethod: 'phone',
      countryCode: formData.phoneData.countryCode,
      countryIsoCode: formData.phoneData.countryIsoCode,
      number: formData.phoneData.number,
    })

    await signInWithPhone({
      phone_number: completePhone,
    })
  }

  const handleMessagingMethodSelected = (method: MessagingMethodCode) => {
    setMessagingMethod(method)
    setSelectedMessagingMethod(method)
    handleSendCodeWithMethod(method)
  }

  const handleSendCodeWithMethod = async (method: MessagingMethodCode) => {
    if (!pendingSignUpData) return

    await sendCodeToPhone({
      phone_number: pendingSignUpData.phoneNumber,
      send_method: method,
    })
  }

  const isLoading = isCheckingPhone || isSendingCode

  return (
    <>
      <BaseView gap={8}>
        <PhoneNumberInput
          value={getValues('phoneData')}
          onChange={(phoneData: PhoneData) => {
            setValue('phoneData', phoneData, {
              shouldValidate: phoneData.number.length > 5,
            })
            setCurrentPhoneData(phoneData)
            setAuthPreferences({
              signInMethod: 'phone',
              countryCode: phoneData.countryCode,
              countryIsoCode: phoneData.countryIsoCode,
              number: phoneData.number,
            })
          }}
          withoutBorder
          isLoading={isLoadingCountries}
          errorMessage={
            errorMessage ||
            formState.errors.phoneData?.number?.message ||
            formState.errors.phoneData?.countryCode?.message ||
            formState.errors.phoneData?.countryIsoCode?.message ||
            ''
          }
        />
      </BaseView>

      <BaseView mt="auto" row align="center" gap={12} className="mb-3">
        <AppButton
          label={t('form:actions.useEmail')}
          disabled={isLoading}
          type="secondaryGrayDark"
          onPress={() => {
            setIdentificationMethod('email')
            setAuthPreferences({
              signInMethod: 'email',
            })

            navigate('/auth/identifier/with-email')
          }}
          style={{ flex: 1 }}
        />
        <AppButton
          label={t('form:actions.next')}
          onPress={handleSubmit(handleSubmitForm)}
          disabled={
            !phoneIsValid || !formState.isValid || !currentPhoneData?.number
          }
          isLoading={isLoading}
          style={{ flex: 1 }}
        />
      </BaseView>

      <MessagingMethodsBottomSheet
        type="recoverPassword"
        onMethodSelected={handleMessagingMethodSelected}
        isOpen={showMessagingMethodsSheet}
        onClose={() => {
          setShowMessagingMethodsSheet(false)
          setPendingSignUpData(null)
        }}
        isLoading={isSendingCode}
        selectedMessagingMethod={selectedMessagingMethod}
        setSelectedMessagingMethod={setSelectedMessagingMethod}
      />
    </>
  )
}
